import { AlertTriangle, Shield, ArrowLeft, Mail } from 'lucide-react'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'

interface UnauthorizedAccessModalProps {
  isOpen: boolean
  onClose: () => void
  userEmail?: string
  userRole?: string
  onGoBack?: () => void
}

export function UnauthorizedAccessModal({ 
  isOpen, 
  onClose, 
  userEmail, 
  userRole,
  onGoBack 
}: UnauthorizedAccessModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-full">
              <Shield className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                Access Denied
              </DialogTitle>
              <DialogDescription className="text-gray-600 dark:text-gray-400">
                You don't have permission to access the admin panel
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* User Info Card */}
          <Card className="border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
                  Current User Information
                </span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="h-3 w-3 text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Email:</strong> {userEmail || 'Not available'}
                  </span>
                </div>
                
                <div className="flex items-center gap-2">
                  <Shield className="h-3 w-3 text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Role:</strong> 
                    <Badge 
                      variant={userRole === 'admin' ? 'default' : 'secondary'} 
                      className="ml-2 text-xs"
                    >
                      {userRole || 'Unknown'}
                    </Badge>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Access Requirements */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
              Admin Access Requirements
            </h4>
            <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
              <li>• Your account must have admin privileges</li>
              <li>• Admin status is managed by system administrators</li>
              <li>• Contact your administrator to request access</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {onGoBack && (
              <Button 
                onClick={onGoBack}
                className="flex items-center gap-2"
                variant="default"
              >
                <ArrowLeft className="h-4 w-4" />
                Go Back to Chat
              </Button>
            )}
            
            <Button 
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Close
            </Button>
          </div>

          {/* Footer Note */}
          <div className="text-center pt-2 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              If you believe this is an error, please contact your system administrator
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
